import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  Subject,
  Subscription,
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  take,
  takeUntil,
  tap,
} from 'rxjs';
import { Router, UrlTree } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { TuiAlertService, TuiDialogService } from '@taiga-ui/core';

import { Zone } from 'src/app/models/zone/zone.model';
import { GanttTask } from '../../interfaces/Gant.interface';
import { NodeStatusUpdateRequest } from 'src/app/models/service-manager/node-status.model';

import { GanttService } from 'src/app/services/gantt/gantt.service';
import { ServicesService } from 'src/app/services/service/services.service';
import {
  formatHourISO,
  humanizeDuration,
  parseGrpcLocal,
  PAT_ADDR,
  PAT_TIME,
  PAT_V_TEXT,
} from 'src/app/utils';
import { grayInfoIcon } from 'src/app/utils/icons-path';
import {
  DelayType,
  DriverInformation,
  GetSideBarGanttDataResponse,
  ServiceLocation,
  UpdateSidebarRequest,
} from '../../interfaces/gantt-details.model';
import { VehicleChassisLabel } from 'src/app/enum/vehicle-chassis-type.enum';
import { VehicleFuelLabelKeys } from 'src/app/enum/vehicle-fuel.enum';
import { NgbModal, NgbOffcanvas } from '@ng-bootstrap/ng-bootstrap';
import { ModalAssignDriverComponent } from 'src/app/shared/modal-assign-driver/modal-assign-driver.component';
import { AssignDriverServiceInfo } from 'src/app/models/driver/driver.model';
import { UnassignDriverModalComponent } from '../unassign-driver-modal/unassign-driver-modal.component';
import { UnassignDriverModalData } from '../unassign-driver-modal/models/unassign-driver-model';
import {
  STATUS_CODE_MAP,
  ORDER_STATUS_MAP,
} from 'src/app/constants/order-status';
import { ModalDelayedServiceComponent } from 'src/app/shared/modal-delayed-service/modal-delayed-service.component';
import { DelayedServiceService } from 'src/app/shared/modal-delayed-service/service/delayed-service.service';
import { ModalDelayedServiceUncheckComponent } from 'src/app/shared/modal-delayed-service-uncheck/modal-delayed-service-uncheck.component';
import { isValidGuid } from 'src/app/utils/guid-validator';
import { ModalCancelServiceComponent } from 'src/app/shared/modal-cancel-service/modal-cancel-service.component';
import { PermissionsService } from 'src/app/services/permissions/permissions.service';
import {
  ServiceTypeMobility,
  ServiceTypeMobilityLabelKey,
} from 'src/app/enum/service-type-mobility.enum';
import { ModalFailedServiceComponent } from 'src/app/pages/detail-service/components/modals/modal-failed-service/modal-failed-service.component';
import {
  FailedServiceOption,
  FailedServiceService,
} from 'src/app/pages/detail-service/components/modals/modal-failed-service/failed-service.service';
import { OrderStatusEnum } from 'src/app/enum/order-status/order-status.enum';

declare const google: any;

const HH_MM = /^([01]\d|2[0-3]):[0-5]\d$/;
const SAME_SPOT_METERS = 10;

export enum MapKind {
  Public = 'completePublicTransportModeMap',
  Driver = 'caflerDrivingModeMap',
}

type FormField =
  | 'clientName'
  | 'zoneName'
  | 'pickupAddress'
  | 'dropoffAddress'
  | 'date'
  | 'schedule'
  | 'vehicleBrand'
  | 'vehicleModel'
  | 'licensePlate';

@Component({
  selector: 'app-gantt-sidebar',
  templateUrl: './gantt-sidebar.component.html',
  styleUrls: ['./gantt-sidebar.component.css'],
})
export class GanttSidebarComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input({ required: true }) service!: GanttTask;
  @Input({ required: true }) zoneId!: string;
  @Input({ required: true }) zones!: Zone[];
  @Input({ required: true }) dateService!: Date;
  @Input() originNodeId?: string;
  @Input() isService!: boolean;
  @Input() isGanttLoading: boolean = false;

  @Output() updateGantt = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();

  isCopec: boolean = false;

  readonly OrderStatusEnum = OrderStatusEnum;

  private pickupAC?: google.maps.places.Autocomplete;
  private dropoffAC?: google.maps.places.Autocomplete;

  /* ViewChild como SETTER: se ejecuta cada vez que el *ngIf inserta-quita */
  @ViewChild('pickupInput')
  set pickupInputSetter(el: ElementRef<HTMLInputElement> | undefined) {
    if (el && !this.pickupAC) {
      this.pickupAC = this.createAutocomplete(el, 'pickupAddress');
    }
  }

  @ViewChild('dropoffInput')
  set dropoffInputSetter(el: ElementRef<HTMLInputElement> | undefined) {
    if (el && !this.dropoffAC) {
      this.dropoffAC = this.createAutocomplete(el, 'dropoffAddress');
    }
  }

  @ViewChild('actionsRoot')
  private actionsRoot?: ElementRef<HTMLElement>;

  @HostListener('document:click', ['$event.target'])
  onGlobalClick(target: EventTarget | null): void {
    if (
      this.showMenu &&
      target instanceof Node &&
      !this.actionsRoot?.nativeElement.contains(target)
    ) {
      this.showMenu = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.showMenu = false;
  }

  @ViewChild('gmap', { read: ElementRef })
  private set gmapSetter(el: ElementRef<HTMLDivElement> | undefined) {
    if (!el) {
      this.destroyMapArtifacts();
      this.map = undefined as any;
      this.gmap = undefined;
      return;
    }

    this.gmap = el;
    this.initMap(el.nativeElement);
  }

  details$!: Observable<GetSideBarGanttDataResponse>;
  details: GetSideBarGanttDataResponse | null = null;

  delayedGuidValid: boolean = false;
  delayType = DelayType;

  loading: boolean = true;
  loadError: boolean = false;
  editMode: boolean = false;
  saving: boolean = false;
  submitted: boolean = false;
  isEditingGanttDetails: boolean = false;
  showMenu: boolean = false;
  isMapLoading = false;

  form!: FormGroup;

  private gmap?: ElementRef<HTMLDivElement>;

  mapType: MapKind = MapKind.Driver;
  readonly mapKind = MapKind;

  VehicleChassisLabel = VehicleChassisLabel;
  VehicleFuelLabelKeys = VehicleFuelLabelKeys;

  private map!: google.maps.Map;
  private directionsRenderer!: google.maps.DirectionsRenderer;
  private customMarkers: google.maps.Marker[] = [];
  private providerMarkers: google.maps.Marker[] = [];

  durationTotal: number = 0;
  durationTransit: number = 0;
  durationWalking: number = 0;

  human = humanizeDuration;

  /* Estado de cálculo ruta */
  readonly _warnKey$ = new BehaviorSubject<string | null>(null);

  readonly routeWarningKey$ = this._warnKey$
    .asObservable()
    .pipe(distinctUntilChanged());

  sameSpotNoticeKey: string | null = null;
  private routeDate!: Date;
  grayInfoIcon = grayInfoIcon;

  /* ════════════════════  LOCK NODE / DRIVERS ═════════════════════════ */
  isNodeLockedForDriverAssignation = true;
  isNodeLockedForSweeperAssignation = false;
  nodeStatusLoading = false;

  private initialDurationMinutes = 0;
  private initialServiceDurationMinutes = 0;
  private readonly destroy$ = new Subject<void>();

  private startWatcherSub?: Subscription;
  private serviceStartWatcherSub?: Subscription;

  // Lista de estados (code + key + label) para el selector de nodeStatus
  public statuses: Array<{ code: number; key: string; label: string }> = [];

  private selectedFailureReasonGuid: string | null = null;
  private selectedOtherReason: string | null = null;

  private tooltipEl?: HTMLElement;
  private tooltipId = 0;

  isValidGuid = isValidGuid;


  warningIcon = 'assets/icons/warning.svg';

  whiteFlagIcon = 'assets/icons/flag-off.svg';

  constructor(
    private readonly fb: FormBuilder,
    private readonly router: Router,
    private readonly ganttSvc: GanttService,
    private readonly servicesSvc: ServicesService,
    private readonly alerts: TuiAlertService,
    private readonly dialogs: TuiDialogService,
    private readonly translate: TranslateService,
    private readonly offcanvasService: NgbOffcanvas,
    private readonly modalService: NgbModal,
    private readonly ngZone: NgZone,
    private readonly renderer: Renderer2,
    private readonly delayedService: DelayedServiceService,
    private readonly permissionsService: PermissionsService,
    private readonly failedService: FailedServiceService
  ) {}

  ngOnInit(): void {
    this.checkTransportType();
    this.buildForm();

    // Construimos lista de estados para el select de nodeStatus
    this.statuses = Object.entries(STATUS_CODE_MAP).map(([code, key]) => ({
      code: Number(code),
      key,
      label: ORDER_STATUS_MAP[key]?.label ?? key,
    }));

    this.permissionsService
      .isCopec()
      .pipe(takeUntil(this.destroy$))
      .subscribe((isCopec) => {
        this.isCopec = isCopec;
      });

    this.form
      .get('nodeStatus')
      ?.valueChanges.pipe(
        filter((value) => value === OrderStatusEnum.FAILED && this.editMode),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.openFailedModal();
      });

    this.watchStartTimeChanges();
    this.watchServiceStartTimeChanges();

    this.details$ = this.fetchSidebarDetails();
  }

  ngOnDestroy(): void {
    this.hideManualTooltip();
    this.destroyMapArtifacts();
    this.destroy$.next();
    this.destroy$.complete();
    this.startWatcherSub?.unsubscribe();
    this.serviceStartWatcherSub?.unsubscribe();
  }

  private openFailedModal(): void {
    const modalRef = this.modalService.open(ModalFailedServiceComponent, {
      size: 'md',
      centered: true,
      backdrop: 'static',
      keyboard: false,
    });

    const component = modalRef.componentInstance;
    component.orderHash = this.service?.orderHash ?? '';
    component.nodeId = this.originNodeId;
    component.autoSave = false; 

    component.reasonSelected.subscribe(
      (result: { reasonId: string; reasonText: string | null }) => {
        this.selectedFailureReasonGuid = result.reasonId;
        this.selectedOtherReason = result.reasonText;
      }
    );

    modalRef.result.catch(() => {
      this.form.get('nodeStatus')?.setValue(null);
    });
  }

  async removeServiceLateStatus(): Promise<void> {
    const orderHash = this.service?.orderHash;

    if (!orderHash) {
      return;
    }

    if ((await this.confirmUncheckDelayed()) === false) {
      return;
    }

    this.delayedService.removeServiceDelay(orderHash).subscribe({
      next: () => {
        this.alerts
          .open(this.translate.instant('service_manager.delay.remove_ok'), {
            status: 'success',
            autoClose: true,
          })
          .subscribe();

        this.updateGantt.emit();
        this.reloadGanttDetails();
      },
      error: (error: unknown) => {
        console.error('[GanttSidebar] removeServiceLateStatus error', error);

        this.alerts
          .open(this.translate.instant('service_manager.delay.remove_error'), {
            status: 'error',
            autoClose: true,
          })
          .subscribe();
      },
    });
  }

  private checkTransportType(): void {
    this.mapType =
      this.service.nodeType === 'TRANSPORT_NODE'
        ? MapKind.Public
        : MapKind.Driver;
  }

  ngAfterViewInit(): void {
    if (!window['google']?.maps?.places) {
      console.error('Google Maps Places API not loaded');
      return;
    }
  }

  toggleMenu(ev: MouseEvent): void {
    ev.stopPropagation();
    this.showMenu = !this.showMenu;
  }

  private emitWarn(key: string | null): void {
    setTimeout(() => this._warnKey$.next(key), 0);
  }

  addressSelectedValidator(
    addressCtrl: string,
    latCtrl: string,
    lngCtrl: string
  ): ValidatorFn {
    return (group: AbstractControl): ValidationErrors | null => {
      const addr = group.get(addressCtrl)?.value as string;
      const lat = group.get(latCtrl)?.value as number | null;
      const lng = group.get(lngCtrl)?.value as number | null;

      return addr && (lat === null || lng === null)
        ? { [`${addressCtrl}NotSelected`]: true }
        : null;
    };
  }

  modifityLateService(): void {
    this.showMenu = false;
    const ref = this.modalService.open(ModalDelayedServiceComponent, {
      backdrop: 'static',
      centered: true,
      windowClass: 'gray',
    });

    ref.componentInstance.orderHash = this.service.orderHash;
    ref.componentInstance.nodeId = this.service.nodeId;

    // Pasar valores existentes si hay detalles cargados
    if (this.details) {

      ref.componentInstance.existingDelayType = this.details.delayType;
      ref.componentInstance.existingReasonGuid = this.details.delayReasonGuid;
    }

    ref.closed.subscribe((result: unknown) => {
      if (result === 'saved') {
        this.updateGantt.emit();
        this.closed.emit();
        this.reloadGanttDetails();
        const checkGanttLoading = setInterval(() => {
          if (!this.isGanttLoading) {
            clearInterval(checkGanttLoading);
            this.alertOk('service_manager.delay.saved_ok');
          }
        }, 100);
      }
    });
  }

  cancelService(): void {
    const ref = this.modalService.open(ModalCancelServiceComponent, {
      backdrop: 'static',
      centered: true,
      windowClass: 'gray',
    });

    ref.componentInstance.orderHash = this.service.orderHash;
    ref.componentInstance.nodeId = this.service.nodeId;

    ref.closed.subscribe((result: unknown) => {
      if (result === 'saved') {
        this.alertOk('service_manager.cancel.cancel_success');
        this.updateGantt.emit();
        this.reloadGanttDetails();
      }
    });
  }

  confirmUncheckDelayed(): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      const ref = this.modalService.open(ModalDelayedServiceUncheckComponent, {
        backdrop: 'static',
        centered: true,
        windowClass: 'gray',
      });

      ref.closed.subscribe((result?: boolean) => {
        resolve(!!result);
      });
    });
  }

  private createAutocomplete(
    elRef: ElementRef<HTMLInputElement>,
    controlName: 'pickupAddress' | 'dropoffAddress'
  ): google.maps.places.Autocomplete {
    const ac = new google.maps.places.Autocomplete(elRef.nativeElement, {
      types: ['address'],
      fields: [
        'address_components',
        'formatted_address',
        'geometry',
        'name',
        'place_id',
      ],
    });

    type Ctrl =
      | 'pickupAddress'
      | 'dropoffAddress'
      | 'pickupLat'
      | 'pickupLng'
      | 'dropoffLat'
      | 'dropoffLng';

    const prefix = controlName.startsWith('pickup') ? 'pickup' : 'dropoff';
    const latCtrl = `${prefix}Lat` as Ctrl;
    const lngCtrl = `${prefix}Lng` as Ctrl;

    const typingListener = () => {
      this.ngZone.run(() =>
        this.form.patchValue(
          { [latCtrl]: null, [lngCtrl]: null } as Partial<
            Record<Ctrl, number | null>
          >,
          { emitEvent: false }
        )
      );
    };
    elRef.nativeElement.addEventListener('input', typingListener);

    const placeListener = ac.addListener('place_changed', () => {
      this.ngZone.run(() => {
        const place = ac.getPlace();
        if (!place.geometry) return; // seguridad

        this.form.patchValue(
          {
            [controlName]: place.formatted_address ?? '',
            [latCtrl]: place.geometry.location.lat(),
            [lngCtrl]: place.geometry.location.lng(),
          } as Partial<Record<Ctrl, string | number>>,
          { emitEvent: false }
        );
      });
    });

    this.destroy$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      google.maps.event.removeListener(placeListener);
      elRef.nativeElement.removeEventListener('input', typingListener);
    });

    elRef.nativeElement.setAttribute('autocomplete', 'off');

    return ac;
  }

  private buildForm(): void {
    this.form = this.fb.group(
      {
        clientName: ['', Validators.required],
        zoneName: ['', Validators.required],
        pickupAddress: ['', [Validators.required]],
        pickupLat: [null as number | null, Validators.required],
        pickupLng: [null as number | null, Validators.required],
        dropoffAddress: ['', [Validators.required]],
        dropoffLat: [null as number | null, Validators.required],
        dropoffLng: [null as number | null, Validators.required],
        date: ['', Validators.required],
        startTime: [
          null as string | null,
          [
            Validators.required,
            Validators.pattern(PAT_TIME),
            Validators.maxLength(5),
          ],
        ],
        endTime: [
          null as string | null,
          [
            Validators.required,
            Validators.pattern(PAT_TIME),
            Validators.maxLength(5),
          ],
        ],
        vehicleBrand: [''],
        vehicleModel: [''],
        licensePlate: [''],

        nodeStatus: [null],
        serviceStartTime: [
          null as string | null,
          [Validators.pattern(PAT_TIME), Validators.maxLength(5)],
        ],
        serviceEndTime: [
          null as string | null,
          [Validators.pattern(PAT_TIME), Validators.maxLength(5)],
        ],
      },
      {
        validators: [
          this.endAfterStartValidator,
          this.addressSelectedValidator(
            'pickupAddress',
            'pickupLat',
            'pickupLng'
          ),
          this.addressSelectedValidator(
            'dropoffAddress',
            'dropoffLat',
            'dropoffLng'
          ),
        ],
        updateOn: 'blur',
      }
    );
  }

  endAfterStartValidator: ValidatorFn = (
    group: AbstractControl
  ): ValidationErrors | null => {
    const startCtrl = group.get('startTime');
    const endCtrl = group.get('endTime');
    if (!startCtrl || !endCtrl) return null;

    const start = startCtrl.value as string;
    const end = endCtrl.value as string;
    if (!HH_MM.test(start) || !HH_MM.test(end)) return null;

    const [sh, sm] = start.split(':').map(Number);
    const [eh, em] = end.split(':').map(Number);
    const startMins = sh * 60 + sm;
    const endMins = eh * 60 + em;

    if (endCtrl.errors?.['endBeforeStart']) {
      const { endBeforeStart, ...rest } = endCtrl.errors;
      endCtrl.setErrors(Object.keys(rest).length ? rest : null);
    }

    if (endMins < startMins) {
      endCtrl.setErrors({ ...endCtrl.errors, endBeforeStart: true });
      return { endBeforeStart: true };
    }

    return null;
  };

  areSameCoords(
    a: google.maps.LatLngLiteral,
    b: google.maps.LatLngLiteral
  ): boolean {
    return (
      google.maps.geometry.spherical.computeDistanceBetween(
        new google.maps.LatLng(a),
        new google.maps.LatLng(b)
      ) < SAME_SPOT_METERS
    );
  }

  onTimeTyped(
    ev: Event,
    controlName: 'startTime' | 'endTime' | 'serviceStartTime' | 'serviceEndTime'
  ) {
    const input = ev.target as HTMLInputElement;
    let v = input.value.replace(/\D/g, '');

    if (v.length > 4) {
      v = v.slice(0, 4);
    }

    if (v.length > 2) {
      v = v.slice(0, 2) + ':' + v.slice(2);
    }

    if (/^\d{2}:\d{2}$/.test(v)) {
      const [h, m] = v.split(':').map(Number);
      if (h > 23 || m > 59) {
        v = this.form.get(controlName)!.value || '';
      }
    }

    input.value = v;
    this.form.get(controlName)!.setValue(v);
  }

  formatDate(iso: string): string {
    const d = new Date(iso);
    return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear()}`;
  }

  public getNodeStatusLabel(nodeStatus?: number | null): string {
    if (nodeStatus === undefined || nodeStatus === null) {
      return '–';
    }

    const semanticKey = STATUS_CODE_MAP[nodeStatus];

    if (semanticKey) {
      const semanticTranslateKey = `service_manager.gantt_sidebar.node_status.${semanticKey}`;
      const semanticTranslated = this.translate.instant(semanticTranslateKey);
      if (semanticTranslated && semanticTranslated !== semanticTranslateKey) {
        return semanticTranslated;
      }
    }

    const numericTranslateKey = `service_manager.gantt_sidebar.node_status.${nodeStatus}`;
    const numericTranslated = this.translate.instant(numericTranslateKey);
    if (numericTranslated && numericTranslated !== numericTranslateKey) {
      return numericTranslated;
    }

    const genericKeyCandidate = semanticKey ?? String(nodeStatus);
    const genericTranslateKey = `service_manager.gantt_sidebar.node_status_generic.${genericKeyCandidate}`;
    const genericTranslated = this.translate.instant(genericTranslateKey);
    if (genericTranslated && genericTranslated !== genericTranslateKey) {
      return genericTranslated;
    }

    return semanticKey ?? String(nodeStatus);
  }

  private fetchSidebarDetails(): Observable<GetSideBarGanttDataResponse> {
    this.loading = true;
    this.loadError = false;

    return this.ganttSvc
      .getSidebarData(this.service.nodeId, this.isService, this.originNodeId)
      .pipe(
        tap((details) => {
          this.syncServiceAndRedraw(details);

          this.details = details;

          this.delayedGuidValid = isValidGuid(this.details?.delayReasonGuid);

          this.patchForm(details);

          this.isNodeLockedForDriverAssignation =
            details.routeInformation.driverData
              ?.isNodeLockedForDriverAssignation ?? false;
          this.isNodeLockedForSweeperAssignation =
            details.routeInformation.sweeperDriverData
              ?.isNodeLockedForDriverAssignation ??
            details.routeInformation.sweeperDriverData
              ?.isNodeLockedForSweeperDriverAssignation ??
            details.routeInformation.sweeperDriverData
              ?.isNodeLockedForSweeperAssignation ?? false;
          this.routeDate =
            parseGrpcLocal(details.routeInformation.assignedDate) ?? new Date();

          this.loading = false;
        }),
        catchError((err) => {
          console.error('Sidebar API error:', err);
          this.loadError = true;
          return EMPTY;
        }),
        finalize(() => (this.loading = false))
      );
  }

  private syncServiceAndRedraw(details: GetSideBarGanttDataResponse): void {
    this.service = {
      ...this.service,
      nodeId: details.nodeId,
      productName: details.productName,
      startLatitude: details.originLocation.latitude,
      startLongitude: details.originLocation.longitude,
      endLatitude: details.destinationLocation.latitude,
      endLongitude: details.destinationLocation.longitude,
      licensePlate: details.vehicleInformation.licensePlate,
    };

    if (this.map) {
      this.destroyMapArtifacts();
      this.drawRoute();
    }
  }

  reloadGanttDetails(): void {
    this.details$ = this.fetchSidebarDetails();
    this.details$
      .pipe(
        tap(() => {
          if (this.map) {
            this.destroyMapArtifacts();
            this.drawRoute();
          }
        })
      )
      .subscribe();
  }

  private calculateInitialDuration(
    assignedIso: string | { completeDate?: string | null } | null,
    endIso: string | { completeDate?: string | null } | null
  ): void {
    const startDt = parseGrpcLocal(assignedIso);
    const endDt = parseGrpcLocal(endIso);
    if (startDt && endDt) {
      const diffMs = endDt.getTime() - startDt.getTime();
      this.initialDurationMinutes = diffMs > 0 ? diffMs / 60000 : 30;
    } else {
      this.initialDurationMinutes = 30;
    }
  }

  private calculateInitialServiceDuration(
    serviceStartIso:
      | string
      | { completeDate?: string | null | undefined }
      | null,
    serviceEndIso: string | { completeDate?: string | null | undefined } | null
  ): void {
    const startDt = parseGrpcLocal(serviceStartIso);
    const endDt = parseGrpcLocal(serviceEndIso);
    if (startDt && endDt) {
      const diffMs = endDt.getTime() - startDt.getTime();
      this.initialServiceDurationMinutes = diffMs > 0 ? diffMs / 60000 : 30;
    } else {
      this.initialServiceDurationMinutes = 30;
    }
  }

  private watchStartTimeChanges(): void {
    this.startWatcherSub?.unsubscribe();
    this.startWatcherSub = this.form
      .get('startTime')!
      .valueChanges.pipe(
        debounceTime(200),
        filter((v: string) => PAT_TIME.test(v)),
        takeUntil(this.destroy$)
      )
      .subscribe((newStart) => this.applyDurationToEnd(newStart));
  }

  // observador para serviceStartTime -> ajusta serviceEndTime con misma duración inicial
  private watchServiceStartTimeChanges(): void {
    this.serviceStartWatcherSub?.unsubscribe();
    this.serviceStartWatcherSub = this.form
      .get('serviceStartTime')!
      .valueChanges.pipe(
        debounceTime(200),
        filter((v: string) => PAT_TIME.test(v)),
        takeUntil(this.destroy$)
      )
      .subscribe((newStart) => this.applyDurationToServiceEnd(newStart));
  }

  private applyDurationToEnd(start: string): void {
    const dateStr = this.form.get('date')!.value as string;
    const [yyyy, mm, dd] = dateStr.split('-').map(Number);
    if (!yyyy) return;
    const [h, m] = start.split(':').map(Number);
    const startDt = new Date(yyyy, mm - 1, dd, h, m);
    const endDt = new Date(
      startDt.getTime() + this.initialDurationMinutes * 60000
    );

    const hh = endDt.getHours().toString().padStart(2, '0');
    const mi = endDt.getMinutes().toString().padStart(2, '0');

    this.form.patchValue({ endTime: `${hh}:${mi}` }, { emitEvent: false });
  }

  private applyDurationToServiceEnd(start: string): void {
    const dateStr = this.form.get('date')!.value as string;
    const [yyyy, mm, dd] = dateStr.split('-').map(Number);
    if (!yyyy) return;
    const [h, m] = start.split(':').map(Number);
    const startDt = new Date(yyyy, mm - 1, dd, h, m);
    const endDt = new Date(
      startDt.getTime() + this.initialServiceDurationMinutes * 60000
    );

    const hh = endDt.getHours().toString().padStart(2, '0');
    const mi = endDt.getMinutes().toString().padStart(2, '0');

    this.form.patchValue(
      { serviceEndTime: `${hh}:${mi}` },
      { emitEvent: false }
    );
  }

  private patchForm(ui: GetSideBarGanttDataResponse): void {
    const {
      routeInformation,
      originLocation,
      destinationLocation,
      vehicleInformation,
      clientName,
      zoneName,
    } = ui;

    const dtStart = parseGrpcLocal(routeInformation.assignedDate);
    const dtEnd = parseGrpcLocal(routeInformation.endDate);

    const dtServiceStart =
      parseGrpcLocal(ui.startDate) ??
      parseGrpcLocal(routeInformation.assignedDate);
    const dtServiceEnd =
      parseGrpcLocal(ui.endDate) ?? parseGrpcLocal(routeInformation.endDate);

    const ganttDetailDate = dtStart ? dtStart.toISOString().split('T')[0] : '';

    this.form.patchValue(
      {
        /* Datos generales */
        clientName,
        zoneName,

        /* Origen */
        pickupAddress: originLocation.address,
        pickupLat: originLocation.latitude ?? null,
        pickupLng: originLocation.longitude ?? null,

        /* Destino */
        dropoffAddress: destinationLocation.address,
        dropoffLat: destinationLocation.latitude ?? null,
        dropoffLng: destinationLocation.longitude ?? null,

        /* Fecha + horas (hora de recogida = assignedDate) */
        date: ganttDetailDate,
        startTime: dtStart ? formatHourISO(dtStart) : '',
        endTime: dtEnd ? formatHourISO(dtEnd) : '',

        /* Vehículo */
        vehicleBrand: vehicleInformation.brand,
        vehicleModel: vehicleInformation.model,
        licensePlate: vehicleInformation.licensePlate,

        /* Node status (nuevo) */
        nodeStatus: ui.nodeStatus ?? null,

        serviceStartTime: dtServiceStart ? formatHourISO(dtServiceStart) : '',
        serviceEndTime: dtServiceEnd ? formatHourISO(dtServiceEnd) : '',
      },
      {
        emitEvent: false,
        onlySelf: true,
      }
    );

    this.form.markAsPristine();
    this.form.updateValueAndValidity({ emitEvent: false });

    this.calculateInitialDuration(
      ui.routeInformation.assignedDate,
      ui.routeInformation.endDate
    );
    this.calculateInitialServiceDuration(
      ui.startDate ?? null,
      ui.endDate ?? null
    );

    this.watchStartTimeChanges();
    this.watchServiceStartTimeChanges();
  }

  openServiceDetail(): void {
    this.showMenu = false;
    const tree: UrlTree = this.router.createUrlTree(
      ['/service-manager/detail-service'],
      { queryParams: { orderHash: this.service.orderHash } }
    );
    window.open(`${location.origin}/#/${tree}`, '_blank');
  }

  // al activar el modo edición repatchamos (para asegurar separación start vs serviceStart)
  toggleEdit(cancel = false): void {
    this.showMenu = false;
    if (cancel && this.form.dirty) {
      this.cancelEditModel();
      return;
    }

    this.editMode = !this.editMode;

    // si acabamos de activar el modo edición, forzamos un patch desde detalles
    // para evitar que los campos se mezclen con datos anteriores en memoria
    if (this.editMode && this.details) {
      this.patchForm(this.details);
    }
  }

  public formatHourGrpc(
    raw?: string | { completeDate?: string | null } | null
  ): string {
    const dt = parseGrpcLocal(raw);
    if (!dt) return '–';

    const hh = dt.getHours().toString().padStart(2, '0');
    const mm = dt.getMinutes().toString().padStart(2, '0');
    return `${hh}:${mm}`;
  }

  public formatDateGrpc(
    raw?: string | { completeDate?: string | null } | null
  ): string {
    const dt = parseGrpcLocal(raw);
    if (!dt) return '–';

    const dd = dt.getDate().toString().padStart(2, '0');
    const mm = (dt.getMonth() + 1).toString().padStart(2, '0');
    return `${dd}/${mm}/${dt.getFullYear()}`;
  }

  reloadAutoCompleteGoogleMaps(): void {
    this.pickupAC = undefined;
    this.dropoffAC = undefined;
  }

  hasError(ctrl: FormField): boolean {
    const c = this.form.get(ctrl);
    return !!c && c.invalid && (this.submitted || c.touched);
  }

  saveChanges(): void {
    /* Validación ---------------------------------------------------- */

    if (this.form.invalid) {
      const invalidControls = Object.entries(this.form.controls)
        .filter(([, ctrl]) => ctrl.invalid)
        .map(([name, ctrl]) => ({ name, errors: ctrl.errors }));


      this.form.markAllAsTouched();
      this.alertError('common.messages.invalid_fields');
      setTimeout(() => {
        const firstInvalid = document.querySelector<HTMLElement>(
          '.ng-invalid.tui-input__native, .ng-invalid.tui-text-area__native, .ng-invalid [tuiTextfieldLabelOutside]'
        );
        firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        firstInvalid?.focus?.();
      });
      return;
    }

    /* Sin cambios --------------------------------------------------- */
    if (this.form.pristine) {
      this.alertError('common.messages.no_changes');
      return;
    }

    /*  Payload ------------------------------------------------------- */
    const payload = this.buildUpdatePayload();
    if (!payload) {
      this.alertError('common.messages.invalid_fields');
      return;
    }

    /* Llamada al servicio ------------------------------------------ */
    this.isEditingGanttDetails = true;
    this.ganttSvc
      .updateSidebar(payload)
      .pipe(finalize(() => (this.isEditingGanttDetails = false)))
      .subscribe({
        next: ({ isSuccess }) => {
          if (isSuccess) {
            this.alertOk();
            this.editMode = false;
            this.reloadAutoCompleteGoogleMaps();

            this.updateGantt.emit();
            this.form.markAsPristine();
            this.reloadGanttDetails();
          } else {
            this.alertError();
          }
        },
        error: (err) => this.alertError('common.messages.generic_error', err),
      });
  }

  parseDMY(dateStr: string): Date | null {
    const [d, m, y] = dateStr.split(/[\/\-]/).map(Number);

    if (!d || !m || !y) return null;
    const dt = new Date(y, m - 1, d);
    return dt.getFullYear() === y &&
      dt.getMonth() === m - 1 &&
      dt.getDate() === d
      ? dt
      : null;
  }

  private toCompleteDate(dateStr: string, timeStr: string): string | null {
    const [yyyy, mm, dd] = dateStr.split('-').map(Number);
    if (!yyyy || !mm || !dd) return null;
    const [hh, min] = timeStr.split(':').map(Number);
    const d = new Date(yyyy, mm - 1, dd, hh, min);

    if (isNaN(d.getTime())) return null;

    const offsetMin = -d.getTimezoneOffset();
    const sign = offsetMin >= 0 ? '+' : '-';
    const absOff = Math.abs(offsetMin);
    const pad = (n: number) => n.toString().padStart(2, '0');
    const offset = `${sign}${pad(Math.floor(absOff / 60))}:${pad(absOff % 60)}`;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

    const datePart = `${yyyy}-${pad(mm)}-${pad(dd)}`;
    return `${datePart}T${timeStr}:00${offset} ${tz}`;
  }

  private buildUpdatePayload(): UpdateSidebarRequest | null {
    const v = this.form.value;

    if (!v.date || !v.startTime || !v.endTime) {
      return null;
    }

    const startComplete = this.toCompleteDate(v.date, v.startTime);
    const endComplete = this.toCompleteDate(v.date, v.endTime);

    if (!startComplete || !endComplete) {
      return null;
    }

    const serviceStartTime = v.serviceStartTime ?? v.startTime;
    const serviceEndTime = v.serviceEndTime ?? v.endTime;

    const serviceStartComplete =
      serviceStartTime && v.date
        ? this.toCompleteDate(v.date, serviceStartTime)
        : null;
    const serviceEndComplete =
      serviceEndTime && v.date
        ? this.toCompleteDate(v.date, serviceEndTime)
        : null;

    const payload: Partial<UpdateSidebarRequest> = {
      originLocation: this.buildLocationObj(
        v.pickupAddress,
        v.pickupLat,
        v.pickupLng
      ),
      destinationLocation: this.buildLocationObj(
        v.dropoffAddress,
        v.dropoffLat,
        v.dropoffLng
      ),

      startDate: { completeDate: startComplete },
      endDate: { completeDate: endComplete },

      ...(serviceStartComplete
        ? { serviceStartDate: { completeDate: serviceStartComplete } }
        : {}),
      ...(serviceEndComplete
        ? { serviceEndDate: { completeDate: serviceEndComplete } }
        : {}),

      brand: v.vehicleBrand,
      model: v.vehicleModel,
      licensePlate: v.licensePlate,
      nodeId: this.service.nodeId,

      ...(typeof v.nodeStatus !== 'undefined' && v.nodeStatus !== null
        ? { nodeStatus: v.nodeStatus }
        : {}),

      ...(v.nodeStatus === OrderStatusEnum.FAILED &&
      this.selectedFailureReasonGuid
        ? { failureReasonGuid: this.selectedFailureReasonGuid }
        : {}),
      ...(v.nodeStatus === OrderStatusEnum.FAILED &&
      this.selectedOtherReason
        ? { otherReason: this.selectedOtherReason.trim() }
        : {}),
    };

    return Object.entries(payload).reduce((acc, [k, val]) => {
      if (typeof val !== 'undefined' && val !== null) (acc as any)[k] = val;
      return acc;
    }, {} as UpdateSidebarRequest);
  }

  private alertOk(messageKey: string = 'common.messages.saved_ok'): void {
    this.alerts
      .open(this.translate.instant(messageKey), {
        status: 'success',
        autoClose: true,
      })
      .subscribe();
  }

  private alertError(
    messageKey: string = 'common.messages.generic_error',
    extra?: string
  ): void {
    const text =
      extra && typeof extra === 'string'
        ? this.translate.instant(messageKey, { error: extra })
        : this.translate.instant(messageKey);

    this.alerts
      .open(text, {
        status: 'error',
        autoClose: true,
      })
      .subscribe();
  }

  private buildLocationObj(
    address: string,
    lat: number | null,
    lng: number | null
  ): ServiceLocation {
    return {
      address,
      latitude: lat ?? 0,
      longitude: lng ?? 0,
    };
  }

  get serviceTypeLabel(): string {
    if (!this.service?.productName) return '–';

    const key = 'common.services.' + this.service.productName;
    const translated = this.translate.instant(key);

    return translated === key ? this.service.productName : translated;
  }

  getMobilityServiceTypeLabelKey(
    serviceTypeMobility: ServiceTypeMobility | string | null | undefined
  ): string | null {
    if (!serviceTypeMobility) return null;
    return (
      ServiceTypeMobilityLabelKey[
        serviceTypeMobility as ServiceTypeMobility
      ] ?? null
    );
  }

  private initMap(container: HTMLDivElement): void {
    this.isMapLoading = true;
    this.destroyMapArtifacts();

    const origin = new google.maps.LatLng(
      this.service.startLatitude,
      this.service.startLongitude
    );

    this.map = new google.maps.Map(container, {
      center: origin,
      zoom: 6,
      disableDefaultUI: true,
      zoomControl: true,
    });

    this.map.addListener('idle', () => (this.isMapLoading = false));

    this.drawRoute();
  }

  private drawSameSpot(pos: google.maps.LatLngLiteral): void {
    const size = new google.maps.Size(32, 32);

    new google.maps.Marker({
      position: pos,
      map: this.map,
      icon: { url: 'assets/img/icon_service2.png', scaledSize: size },
      title: this.translate.instant(
        'service_manager.gantt_sidebar.same_start_end_title'
      ),
    });

    this.map.setZoom(14);
    this.map.panTo(pos);
    this.sameSpotNoticeKey = 'service_manager.gantt_sidebar.same_start_end_msg';
  }

  private drawRoute(): void {
    this.emitWarn(null);
    this.sameSpotNoticeKey = null;

    setTimeout(() => {
      this.durationTotal = 0;
      this.durationTransit = 0;
      this.durationWalking = 0;
    });

    this.updateRouteWarning();

    const origin = {
      lat: this.service.startLatitude,
      lng: this.service.startLongitude,
    };
    const dest = {
      lat: this.service.endLatitude,
      lng: this.service.endLongitude,
    };

    // Filtramos proveedores válidos y quitamos los que coincidan con origen/destino
    const rawProviders = this.details?.providerUbication ?? [];
    const providers = rawProviders.filter(
      (p) =>
        typeof p?.latitude === 'number' &&
        typeof p?.longitude === 'number' &&
        !this.areSameCoords({ lat: p.latitude, lng: p.longitude }, origin) &&
        !this.areSameCoords({ lat: p.latitude, lng: p.longitude }, dest)
    );
    const hasProviders = providers.length > 0;

    // Si O y D son iguales y no hay proveedores, mantenemos el flujo de "mismo punto"
    if (this.areSameCoords(origin, dest) && !hasProviders) {
      this.directionsRenderer?.setMap(null);
      this.drawSameSpot(origin);
      return;
    }

    // Directions
    const ds = new google.maps.DirectionsService();
    this.directionsRenderer = new google.maps.DirectionsRenderer({
      suppressMarkers: this.mapType === MapKind.Driver,
    });
    this.directionsRenderer.setMap(this.map);

    const travelMode =
      this.mapType === MapKind.Driver
        ? google.maps.TravelMode.DRIVING
        : google.maps.TravelMode.TRANSIT;

    const req: google.maps.DirectionsRequest = {
      origin,
      destination: dest,
      travelMode,
      optimizeWaypoints: true,
    };

    // Waypoints SOLO en modo Driver (Google ignora o no soporta waypoints en TRANSIT)
    if (travelMode === google.maps.TravelMode.DRIVING && hasProviders) {
      req.waypoints = providers.slice(0, 23).map((p) => ({
        location: new google.maps.LatLng(p.latitude, p.longitude),
        stopover: true,
      }));
    }

    if (travelMode === google.maps.TravelMode.DRIVING && !this.isPastRoute) {
      req.drivingOptions = {
        departureTime: this.routeDate,
        trafficModel: google.maps.TrafficModel.BEST_GUESS,
      };
    }
    if (
      travelMode === google.maps.DirectionsTravelMode.TRANSIT &&
      !this.outOfRange
    ) {
      req.transitOptions = { departureTime: this.routeDate } as any;
    }

    ds.route(
      req,
      (
        res: google.maps.DirectionsResult,
        status: google.maps.DirectionsStatus
      ) => {
        status === google.maps.DirectionsStatus.OK && res
          ? this.handleRouteSuccess(res)
          : this.handleRouteError(status as google.maps.DirectionsStatus);
      }
    );
  }

  private handleRouteSuccess(result: google.maps.DirectionsResult): void {
    const route = result.routes?.[0];
    if (!route) {
      this.handleRouteError(google.maps.DirectionsStatus.ZERO_RESULTS as any);
      return;
    }

    this.directionsRenderer.setDirections(result);

    let total = 0;
    let transit = 0;
    let walking = 0;

    for (const leg of route.legs ?? []) {
      total += leg.duration?.value ?? 0;

      if (this.mapType === MapKind.Public) {
        for (const step of leg.steps ?? []) {
          const v = step.duration?.value ?? 0;
          if (step.travel_mode === google.maps.TravelMode.TRANSIT) transit += v;
          else if (step.travel_mode === google.maps.TravelMode.WALKING)
            walking += v;
        }
      }
    }

    setTimeout(() => {
      this.durationTotal = total;
      this.durationTransit = transit;
      this.durationWalking = walking;

      if (route.bounds) {
        this.map.fitBounds(route.bounds, 64);
      } else {
        const b = new google.maps.LatLngBounds();
        for (const leg of route.legs ?? []) {
          b.extend(leg.start_location);
          b.extend(leg.end_location);
        }
        this.map.fitBounds(b, 64);
      }

      if (this.mapType === MapKind.Driver) {
        this.addCustomMarkers(result);
      }
      this.updateRouteWarning();
    });
  }

  private handleRouteError(status: google.maps.DirectionsStatus): void {
    this.emitWarn('service_manager.gantt_sidebar.map_generic_error');

    if (
      status === google.maps.DirectionsStatus.ZERO_RESULTS &&
      this.mapType === MapKind.Public
    ) {
      const key = this.isPastRoute
        ? 'service_manager.gantt_sidebar.past_transit_unavailable'
        : this.outOfRange
        ? 'service_manager.gantt_sidebar.future_transit_unavailable'
        : 'service_manager.gantt_sidebar.no_route_found';

      this.emitWarn(key);
      return;
    }

    if (
      status === google.maps.DirectionsStatus.REQUEST_DENIED ||
      status === google.maps.DirectionsStatus.OVER_QUERY_LIMIT
    ) {
      const key =
        status === google.maps.DirectionsStatus.REQUEST_DENIED
          ? 'service_manager.gantt_sidebar.request_denied'
          : 'service_manager.gantt_sidebar.over_query_limit';

      this.emitWarn(key);

      this.alerts
        .open(this.translate.instant(key), {
          status: 'error',
          autoClose: true,
        })
        .subscribe();
    }
  }

  getDriverData(
    details: GetSideBarGanttDataResponse,
    which: 'driver' | 'sweeper'
  ): DriverInformation {
    const data =
      which === 'driver'
        ? details.routeInformation.driverData
        : details.routeInformation.sweeperDriverData;

    return (
      data ?? {
        driverName: '',
        isNodeLockedForDriverAssignation: false,
      }
    );
  }

  isAssigned(drv: DriverInformation): boolean {
    return !!drv.driverName?.trim();
  }

  private addCustomMarkers(res: google.maps.DirectionsResult): void {
    const route = res.routes[0];
    const legs = route.legs;
    if (!route || !legs?.length) return;

    const hoverInfo = new google.maps.InfoWindow();

    const styleCloseButton = (hide = false) => {
      google.maps.event.addListenerOnce(hoverInfo, 'domready', () => {
        const scope: ParentNode = this.gmap?.nativeElement ?? document;
        const nodes = scope.querySelectorAll('.gm-ui-hover-effect');
        const closeBtn = nodes[nodes.length - 1] as HTMLElement | undefined;
        if (!closeBtn) return;

        if (hide) {
          closeBtn.style.display = 'none';
        } else {
          closeBtn.style.transform = 'scale(0.6)';
          closeBtn.style.transformOrigin = 'top right';
          closeBtn.style.margin = '2px';
        }
      });
    };

    const size = new google.maps.Size(30, 30);

    // Origen = inicio del primer leg
    const start = new google.maps.Marker({
      position: legs[0].start_location,
      map: this.map,
      icon: { url: 'assets/img/icon_service.png', scaledSize: size },
      title: this.details?.originLocation?.address || 'Origen',
      zIndex: 1100,
    });
    start.addListener('mouseover', () => {
      const text = this.details?.originLocation?.address || 'Origen';
      hoverInfo.setContent(`<div style="max-width:260px">${text}</div>`);
      hoverInfo.open(this.map, start);
      styleCloseButton(true);
    });
    start.addListener('mouseout', () => hoverInfo.close());

    // Destino = fin del último leg
    const end = new google.maps.Marker({
      position: legs[legs.length - 1].end_location,
      map: this.map,
      icon: { url: 'assets/img/icon_service2.png', scaledSize: size },
      title: this.details?.destinationLocation?.address || 'Destino',
      zIndex: 1100,
    });
    end.addListener('mouseover', () => {
      const text = this.details?.destinationLocation?.address || 'Destino';
      hoverInfo.setContent(`<div style="max-width:260px">${text}</div>`);
      hoverInfo.open(this.map, end);
      styleCloseButton(true);
    });
    end.addListener('mouseout', () => hoverInfo.close());

    this.customMarkers.push(start, end);

    // ── Proveedores (si existen) ─────────────────────────────────────────
    const providers = this.details?.providerUbication ?? [];
    if (!providers.length) return;

    // Letra del proveedor según idioma (ES/EN => P, FR => F)
    const lang = (this.translate?.currentLang ?? 'en').toLowerCase();
    const providerLetter = lang.startsWith('fr') ? 'F' : 'P';

    // Orden real de paradas según Google (si optimizeWaypoints=true)
    const order = Array.isArray(route.waypoint_order)
      ? route.waypoint_order
      : [];
    const limitedProviders = providers.slice(
      0,
      Math.min(providers.length, Math.max(0, legs.length - 1))
    );
    const orderedProviders: ServiceLocation[] = order.length
      ? order.map((i) => limitedProviders[i]).filter(Boolean)
      : limitedProviders;

    // Cada proveedor corresponde al fin de cada leg intermedio (i: 0..legs.length-2)
    for (
      let i = 0;
      i < Math.min(legs.length - 1, orderedProviders.length);
      i++
    ) {
      const prov = orderedProviders[i];
      const m = new google.maps.Marker({
        position: legs[i].end_location,
        map: this.map,
        label: { text: providerLetter },
        title: prov?.address || 'Provider',
        zIndex: 1000,
      });

      m.addListener('mouseover', () => {
        const text = prov?.address || 'Provider';
        hoverInfo.setContent(`<div style="max-width:260px">${text}</div>`);
        hoverInfo.open(this.map, m);
        styleCloseButton(true);
      });
      m.addListener('mouseout', () => hoverInfo.close());

      this.providerMarkers.push(m);
    }
  }

  private destroyMapArtifacts(): void {
    this.directionsRenderer?.setMap(null);
    this.directionsRenderer = undefined as any;
    this.customMarkers.forEach((m) => m.setMap(null));
    this.customMarkers = [];
    this.providerMarkers.forEach((m) => m.setMap(null));
    this.providerMarkers = [];
  }

  openAssignDriverModal(isSweeper: boolean): void {
    const serviceInfo: AssignDriverServiceInfo = {
      mainServiceTypeName: this.service.productName,
      licensePlate: this.service.licensePlate,
    };

    const pickupSeconds = Math.floor(
      new Date(this.routeDate ?? Date.now()).getTime() / 1000
    );

    const offcanvasRef = this.offcanvasService.open(
      ModalAssignDriverComponent,
      {
        backdrop: 'static',
        position: 'end',
      }
    );

    Object.assign(offcanvasRef.componentInstance, {
      orderHash: this.service.orderHash,
      zoneId: this.zoneId,
      nodeId: this.service.nodeId,
      pickupSeconds,
      filter: isSweeper ? 'sweeper' : 'all',
      serviceInfo,
    });

    offcanvasRef.componentInstance.driverAssigned.subscribe(() => {
      this.updateGantt.emit();
      offcanvasRef.close();
      this.reloadGanttDetails();
    });
  }

  openUnassignModal(driver: DriverInformation, isSweeper: boolean): void {
    if (!this.details) return;

    const data: UnassignDriverModalData = {
      nodeId: this.service.nodeId,
      mode: isSweeper ? 'sweeper' : 'driver',
      driverName: driver.driverName,
      startIso: this.details.routeInformation.assignedDate.completeDate ?? null,
      endIso: this.details.routeInformation.endDate?.completeDate ?? null,
    };

    const ref = this.modalService.open(UnassignDriverModalComponent, {
      backdrop: 'static',
      centered: true,
      windowClass: 'gray',
    });

    ref.componentInstance.data = data;

    ref.componentInstance.unassigned.subscribe(() => {
      this.updateGantt.emit();
      ref.close();
      this.reloadGanttDetails();
    });
  }

  private updateRouteWarning(): void {
    let key: string | null = null;

    if (this.mapType === MapKind.Public) {
      if (this.isPastRoute)
        key = 'service_manager.gantt_sidebar.past_transit_unavailable';
      else if (this.outOfRange)
        key = 'service_manager.gantt_sidebar.future_transit_unavailable';
    } else if (this.isPastRoute) {
      key = 'service_manager.gantt_sidebar.past_route_generic';
    }

    setTimeout(() => this._warnKey$.next(key), 0);
  }

  get isPastRoute(): boolean {
    return this.routeDate.getTime() < Date.now() - 60_000;
  }
  get outOfRange(): boolean {
    return this.routeDate.getTime() - Date.now() > 7 * 24 * 60 * 60 * 1000;
  }

  onChangeMapType(mode: MapKind): void {
    if (this.mapType === mode) return;
    this.mapType = mode;
    this.destroyMapArtifacts();
    this.drawRoute();
  }

  openInGoogleMaps(): void {
    const { startLatitude, startLongitude, endLatitude, endLongitude } =
      this.service;

    const url =
      'https://www.google.com/maps/dir/?api=1' +
      `&origin=${encodeURIComponent(`${startLatitude},${startLongitude}`)}` +
      `&destination=${encodeURIComponent(`${endLatitude},${endLongitude}`)}` +
      `&travelmode=${this.mapType === MapKind.Driver ? 'driving' : 'transit'}`;

    window.open(url, '_blank', 'noopener');
  }

  onLockToggle(locked: boolean): void {
    this.isNodeLockedForDriverAssignation = locked;
    setTimeout(() => this.updateNodeStatus(), 0);
  }

  onSweeperLockToggle(locked: boolean, driver: DriverInformation): void {
    this.isNodeLockedForSweeperAssignation = locked;
    driver.isNodeLockedForDriverAssignation = locked;
    driver.isNodeLockedForSweeperDriverAssignation = locked;
    driver.isNodeLockedForSweeperAssignation = locked;
    setTimeout(() => this.updateSweeperNodeStatus(), 0);
  }

  private updateNodeStatus(): void {
    if (!this.service.orderHash || !this.service.nodeId) {
      const msg = this.translate.instant(
        'service_manager.gantt_sidebar.node_locked_missing_data'
      );
      this.alerts.open(msg, { status: 'error', autoClose: true }).subscribe();
      return;
    }

    const payload: NodeStatusUpdateRequest = {
      orderHash: this.service.orderHash,
      nodeId: this.service.nodeId,
      desiredBlockState: this.isNodeLockedForDriverAssignation,
      isSweeperDriver: false
    };

    this.nodeStatusLoading = true;

    this.servicesSvc
      .updateNodeStatus(payload)
      .pipe(finalize(() => (this.nodeStatusLoading = false)))
      .subscribe({
        next: (res) => {
          const ok = res?.wasSuccessful;
          const key = ok
            ? this.isNodeLockedForDriverAssignation
              ? 'service_manager.gantt_sidebar.node_locked_success'
              : 'service_manager.gantt_sidebar.node_unlocked_success'
            : this.isNodeLockedForDriverAssignation
            ? 'service_manager.gantt_sidebar.node_unlocked_error'
            : 'service_manager.gantt_sidebar.node_locked_error';

          this.alerts
            .open(this.translate.instant(key), {
              status: ok ? 'success' : 'error',
              autoClose: true,
            })
            .subscribe();

          if (ok) this.updateGantt.emit();
          else
            this.isNodeLockedForDriverAssignation = !payload.desiredBlockState;
        },
        error: (err) => {
          this.isNodeLockedForDriverAssignation = !payload.desiredBlockState;
          const key = this.isNodeLockedForDriverAssignation
            ? 'service_manager.gantt_sidebar.node_unlocked_error'
            : 'service_manager.gantt_sidebar.node_locked_error';

          this.alerts
            .open(this.translate.instant(key, { error: err.message || err }), {
              status: 'error',
              autoClose: true,
            })
            .subscribe();
        },
      });
  }

  private updateSweeperNodeStatus(): void {
    if (!this.service.orderHash || !this.service.nodeId) {
      const msg = this.translate.instant(
        'service_manager.gantt_sidebar.node_locked_missing_data'
      );
      this.alerts.open(msg, { status: 'error', autoClose: true }).subscribe();
      return;
    }

    const payload: NodeStatusUpdateRequest = {
      orderHash: this.service.orderHash,
      nodeId: this.service.nodeId,
      desiredBlockState: this.isNodeLockedForSweeperAssignation,
      isSweeperDriver: true
    };

    this.nodeStatusLoading = true;

    this.servicesSvc
      .updateNodeStatus(payload)
      .pipe(finalize(() => (this.nodeStatusLoading = false)))
      .subscribe({
        next: (res) => {
          const ok = res?.wasSuccessful;
          const key = ok
            ? this.isNodeLockedForSweeperAssignation
              ? 'service_manager.gantt_sidebar.node_locked_success'
              : 'service_manager.gantt_sidebar.node_unlocked_success'
            : this.isNodeLockedForSweeperAssignation
            ? 'service_manager.gantt_sidebar.node_unlocked_error'
            : 'service_manager.gantt_sidebar.node_locked_error';

          this.alerts
            .open(this.translate.instant(key), {
              status: ok ? 'success' : 'error',
              autoClose: true,
            })
            .subscribe();

          if (ok) {
            this.updateGantt.emit();
          } else {
            this.isNodeLockedForSweeperAssignation = !payload.desiredBlockState;
          }
        },
        error: (err) => {
          this.isNodeLockedForSweeperAssignation = !payload.desiredBlockState;
          const key = this.isNodeLockedForSweeperAssignation
            ? 'service_manager.gantt_sidebar.node_unlocked_error'
            : 'service_manager.gantt_sidebar.node_locked_error';

          this.alerts
            .open(this.translate.instant(key, { error: err.message || err }), {
              status: 'error',
              autoClose: true,
            })
            .subscribe();
        },
      });
  }

  close(): void {
    this.closed.emit();
  }

  cancelEditModel(): void {
    if (this.details) {
      this.patchForm(this.details);
    } else {
      this.form.reset(this.form.getRawValue());
      this.form.markAsPristine();
    }
    this.editMode = false;
    this.reloadAutoCompleteGoogleMaps();
  }

  showManualTooltip(ev: Event, text: string | null): void {
    try {
      ev?.stopPropagation();
    } catch {}

    const trigger = (ev.currentTarget || ev.target) as HTMLElement | null;
    if (!trigger) return;

    this.hideManualTooltip();

    const el = this.renderer.createElement('div') as HTMLElement;
    this.tooltipEl = el;
    el.className = 'manual-tooltip';
    const id = `manual-tooltip-${++this.tooltipId}`;
    el.id = id;
    el.setAttribute('role', 'tooltip');
    el.textContent = text;

    this.renderer.appendChild(document.body, el);

    this.renderer.setAttribute(trigger, 'aria-describedby', id);

    requestAnimationFrame(() => {
      if (!this.tooltipEl) return;
      const tt = this.tooltipEl as HTMLElement;
      const rect = trigger.getBoundingClientRect();
      const ttRect = tt.getBoundingClientRect();

      let top = rect.top - ttRect.height - 8;
      let left = rect.left + rect.width / 2 - ttRect.width / 2;
      let below = false;

      if (top < 8) {
        top = rect.bottom + 8;
        below = true;
      }

      const margin = 8;
      if (left < margin) left = margin;
      if (left + ttRect.width > window.innerWidth - margin)
        left = window.innerWidth - ttRect.width - margin;

      this.renderer.setStyle(tt, 'top', `${Math.round(top)}px`);
      this.renderer.setStyle(tt, 'left', `${Math.round(left)}px`);
      this.renderer.setAttribute(tt, 'data-below', String(below));
    });
  }

  hideManualTooltip(): void {
    if (!this.tooltipEl) return;
    const parent = this.tooltipEl.parentElement;
    if (parent) this.renderer.removeChild(parent, this.tooltipEl);
    this.tooltipEl = undefined;
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.hideManualTooltip();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    this.hideManualTooltip();
  }
}
